import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import Navbar from '../components/Navbar';
import LanguageToggle from '../components/LanguageToggle';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MentorApplicationPage = () => {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    expertise_areas: [],
    experience_years: 0,
    education_background: '',
    cv_url: ''
  });
  
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const expertiseOptions = [
    { value: 'Physics', label: language === 'tr' ? 'Fizik' : 'Physics' },
    { value: 'Mathematics', label: language === 'tr' ? 'Matematik' : 'Mathematics' },
    { value: 'Chemistry', label: language === 'tr' ? 'Kimya' : 'Chemistry' },
    { value: 'Biology', label: language === 'tr' ? 'Biyoloji' : 'Biology' }
  ];

  const handleExpertiseToggle = (expertise) => {
    setFormData(prev => ({
      ...prev,
      expertise_areas: prev.expertise_areas.includes(expertise)
        ? prev.expertise_areas.filter(e => e !== expertise)
        : [...prev.expertise_areas, expertise]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.expertise_areas.length === 0) {
      setError(language === 'tr' ? 'Lütfen en az bir uzmanlık alanı seçin.' : 'Please select at least one expertise area.');
      return;
    }
    
    setLoading(true);
    setError('');

    try {
      await axios.post(`${API}/mentor/apply`, formData, {
        withCredentials: true
      });
      
      setSuccess(true);
      setTimeout(() => {
        navigate('/');
      }, 3000);
    } catch (err) {
      setError(language === 'tr' 
        ? 'Başvurunuz gönderilemedi. Lütfen tekrar deneyin.' 
        : 'Application failed. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="bg-white rounded-2xl shadow-2xl p-12 text-center max-w-md">
          <div className="text-6xl mb-4">✅</div>
          <h2 className="text-3xl font-bold text-gray-800 mb-4">
            {language === 'tr' ? 'Başvurunuz Alındı!' : 'Application Received!'}
          </h2>
          <p className="text-gray-600">
            {language === 'tr' 
              ? 'Başvurunuz başarıyla gönderildi. En kısa sürede size dönüş yapacağız.'
              : 'Your application has been submitted successfully. We will get back to you soon.'
            }
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <LanguageToggle />
      <Navbar title={language === 'tr' ? 'Mentör Başvurusu' : 'Mentor Application'} />

      <div className="max-w-3xl mx-auto px-4 py-8">
        <div className="bg-white rounded-2xl shadow-lg p-8">
          <div className="text-center mb-8">
            <div className="text-6xl mb-4">👨‍🏫</div>
            <h1 className="text-3xl font-bold text-gray-800 mb-2">
              {language === 'tr' ? 'Mentör Başvuru Formu' : 'Mentor Application Form'}
            </h1>
            <p className="text-gray-600">
              {language === 'tr' 
                ? 'Öğrencilerimize rehberlik etmek için başvurun'
                : 'Apply to guide our students'
              }
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Personal Information */}
            <div className="space-y-4">
              <h3 className="text-xl font-semibold text-gray-800">
                {language === 'tr' ? 'Kişisel Bilgiler' : 'Personal Information'}
              </h3>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {language === 'tr' ? 'Ad Soyad' : 'Full Name'} *
                </label>
                <input
                  type="text"
                  required
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  data-testid="full-name-input"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    data-testid="email-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {language === 'tr' ? 'Telefon' : 'Phone'} *
                  </label>
                  <input
                    type="tel"
                    required
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    data-testid="phone-input"
                  />
                </div>
              </div>
            </div>

            {/* Professional Information */}
            <div className="space-y-4">
              <h3 className="text-xl font-semibold text-gray-800">
                {language === 'tr' ? 'Profesyonel Bilgiler' : 'Professional Information'}
              </h3>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {language === 'tr' ? 'Uzmanlık Alanları' : 'Expertise Areas'} *
                </label>
                <div className="grid grid-cols-2 gap-3">
                  {expertiseOptions.map(option => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => handleExpertiseToggle(option.value)}
                      className={`p-3 rounded-lg border-2 transition-all ${
                        formData.expertise_areas.includes(option.value)
                          ? 'border-blue-600 bg-blue-50 text-blue-800'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      data-testid={`expertise-${option.value.toLowerCase()}`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {language === 'tr' ? 'Deneyim (Yıl)' : 'Experience (Years)'} *
                </label>
                <input
                  type="number"
                  required
                  min="0"
                  value={formData.experience_years}
                  onChange={(e) => setFormData({ ...formData, experience_years: parseInt(e.target.value) })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  data-testid="experience-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {language === 'tr' ? 'Eğitim Geçmişi' : 'Education Background'} *
                </label>
                <textarea
                  required
                  value={formData.education_background}
                  onChange={(e) => setFormData({ ...formData, education_background: e.target.value })}
                  rows={4}
                  placeholder={language === 'tr' 
                    ? 'Üniversite, bölüm, derece vb.'
                    : 'University, department, degree, etc.'
                  }
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  data-testid="education-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {language === 'tr' ? 'CV Linki (Opsiyonel)' : 'CV Link (Optional)'}
                </label>
                <input
                  type="url"
                  value={formData.cv_url}
                  onChange={(e) => setFormData({ ...formData, cv_url: e.target.value })}
                  placeholder="https://..."
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  data-testid="cv-url-input"
                />
              </div>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-600">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-4 rounded-xl hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 font-semibold text-lg"
              data-testid="submit-application-button"
            >
              {loading
                ? (language === 'tr' ? 'Gönderiliyor...' : 'Submitting...')
                : (language === 'tr' ? 'Başvuruyu Gönder' : 'Submit Application')
              }
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default MentorApplicationPage;
